import type { LevelDefinition, ProgressV1, RunStats } from './types';

export const STORAGE_KEY = 'mist-harbor-casebook:v1';

export function createEmptyProgress(): ProgressV1 {
  return { version: 1, completed: {} };
}

export function parseProgress(raw: string | null): ProgressV1 {
  if (!raw) return createEmptyProgress();
  try {
    const value = JSON.parse(raw) as unknown;
    if (typeof value !== 'object' || value === null) return createEmptyProgress();
    const candidate = value as Partial<ProgressV1>;
    if (candidate.version !== 1 || typeof candidate.completed !== 'object' || candidate.completed === null) {
      return createEmptyProgress();
    }
    return {
      version: 1,
      completed: candidate.completed,
      ...(typeof candidate.lastLevelId === 'string' ? { lastLevelId: candidate.lastLevelId } : {}),
    };
  } catch {
    return createEmptyProgress();
  }
}

export function loadProgress(storage: Pick<Storage, 'getItem'> = localStorage): ProgressV1 {
  return parseProgress(storage.getItem(STORAGE_KEY));
}

export function saveProgress(progress: ProgressV1, storage: Pick<Storage, 'setItem'> = localStorage): void {
  storage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

export function resetProgress(storage: Pick<Storage, 'removeItem'> = localStorage): ProgressV1 {
  storage.removeItem(STORAGE_KEY);
  return createEmptyProgress();
}

export function isLevelUnlocked(levels: LevelDefinition[], levelId: string, progress: ProgressV1): boolean {
  const index = levels.findIndex((level) => level.id === levelId);
  if (index <= 0) return index === 0;
  const previous = levels[index - 1];
  return previous ? Boolean(progress.completed[previous.id]) : false;
}

export function recordCompletion(progress: ProgressV1, levelId: string, stats: RunStats): ProgressV1 {
  const existing = progress.completed[levelId];
  const nextRecord = {
    bestMoves: existing ? Math.min(existing.bestMoves, stats.moves) : stats.moves,
    bestTimeSeconds: existing ? Math.min(existing.bestTimeSeconds, stats.timeSeconds) : stats.timeSeconds,
    fewestHints: existing ? Math.min(existing.fewestHints, stats.hints) : stats.hints,
    completedAt: existing?.completedAt ?? new Date().toISOString(),
  };
  return {
    version: 1,
    completed: { ...progress.completed, [levelId]: nextRecord },
    lastLevelId: levelId,
  };
}
